import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import Axios, { AxiosProgressEvent } from "axios";
import SparkMD5 from "spark-md5";
import { fileUploadStore } from "./FileUploadStore";
import { message } from "antd";
import { EmailUploadFile } from "Pages/Project/Communication/Stores/EmailSubmitStore";
import i18n, { I18N_NS } from "Config/i18n";

export class FileUploadChunked {
  static CHUNK_SIZE = 1000 * 1000 * 20;
  static UPLOAD_CONSTANSTS: API_ChunkuploadTrigger;
  private uid: string;
  private file: File;
  private details: API_ChunkUploadDetails;
  private offset: number;
  private chunkNumber: number;
  private uploadedChunks: API_FileChunk[];
  private abortSignal: AbortSignal;
  private chunkCount: number;
  private sendChunkPending: boolean;
  private queuedFormData?: FormData;
  resolve: (value?: unknown) => void;
  reject: (reason?: Error) => void;
  public logToConsole: boolean = false;

  constructor(uid: string, file: File, abortSignal: AbortSignal, details: API_ChunkUploadDetails) {
    this.uid = uid;
    this.file = file;
    this.details = details;
    this.offset = 0;
    this.chunkNumber = 0;
    this.uploadedChunks = [];
    this.abortSignal = abortSignal;
    this.chunkCount = Math.ceil(file.size / FileUploadChunked.CHUNK_SIZE);
    this.sendChunkPending = false;
  }

  public init(params): Promise<API_UploadFile[]> {
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;

      API.newData<API_ChunkUploadDetails>(
        API_URL.UPLOAD.initChunkUpload(projectsDataStore.projectId),
        this.details,
        params
      )
        .then((details) => {
          this.details = details;
          this.readNextChunk();
        })
        .catch((reason) => this.handleReject(reason, "cant init cunk upload"));
    });
  }

  static async fetchConstants(projectId: number) {
    try {
      if (!FileUploadChunked.UPLOAD_CONSTANSTS) {
        // its enough to fetch it once, because because they're constants in BE
        FileUploadChunked.UPLOAD_CONSTANSTS = await API.getData<API_ChunkuploadTrigger>(
          API_URL.UPLOAD.getTriggerSize(projectId)
        );
      }
    } catch (e) {
      // default values from BE
      FileUploadChunked.UPLOAD_CONSTANSTS = {
        maxFileSize: 2147483647, // INTEGER.MAX_VALUE
        activeFileSizeLimit: true,
        triggerChunkuploadMB: 1000 * 1000 * 1000, //1GB
      };
    }
  }

  static fileIsNotTooBig(file: File | EmailUploadFile, noWarning?: boolean) {
    if (
      FileUploadChunked.UPLOAD_CONSTANSTS.activeFileSizeLimit &&
      file?.size > FileUploadChunked.UPLOAD_CONSTANSTS.maxFileSize
    ) {
      if (!noWarning) {
        message.warning(
          i18n.t("{{file}} was ignored because its bigger than the allowed maximum file size of {{size}}MB", {
            ns: I18N_NS.PROJECT,
            size: (FileUploadChunked.UPLOAD_CONSTANSTS.maxFileSize / 1000 / 1000).toFixed(),
            file: file.name,
          }) as string
        );
      }
      return false;
    }
    return true;
  }

  private handleReject(error: Error, message: string) {
    console.error(message);
    console.error(error);
    this.reject(error);
    throw error ? error : new Error(message);
  }

  private handleUploadedChunk(uploadedChunk: API_FileChunk) {
    try {
      this.sendChunkPending = false;
      this.uploadedChunks.push(uploadedChunk);
      if (this.uploadedChunks.length === this.chunkCount) {
        API.newData<API_UploadData>(
          API_URL.UPLOAD.finishChunkUpload(projectsDataStore.projectId, this.details.id),
          this.uploadedChunks
        )
          .then((response) => this.handleSuccess(response.files))
          .catch((error) => this.handleReject(error, "error finishing all chunks"));
      } else if (this.queuedFormData) {
        // the next chunk was read already after the previous was sent

        this.sendChunk(this.queuedFormData);
        this.offset += parseInt(this.queuedFormData.get("size").toString());
        this.queuedFormData = undefined;
        if (this.offset < this.file.size) {
          this.readNextChunk();
        }
      }
    } catch (error) {
      this.handleReject(error, "error while handle uploaded chunk ");
    }
  }

  private handleSuccess(files: API_UploadFile[]) {
    try {
      // do it like this, instead of pass the files to this.resolve(files), because we need
      // that additonal try catch here. Otherwise the try catch finally in FileUploadStore.prepareTempFiles
      // won't work correctly, if fileUploadStore.handleSuccess throws an error
      fileUploadStore.handleSuccess(this.uid, files);
      this.resolve();
    } catch (e) {
      this.handleReject(e, "empty files after finish");
    }
  }

  private log(msg: string) {
    if (this.logToConsole) {
      console.log(this.details.id + " " + msg);
    }
  }

  private sendChunk(formData: FormData) {
    this.sendChunkPending = true;
    this.log("send chunk");
    API.newData<API_FileChunk>(
      API_URL.UPLOAD.addFileChunk(projectsDataStore.projectId, this.details.id),
      formData,
      undefined,
      {
        onUploadProgress: (progress: AxiosProgressEvent) => {
          const fakeProgress: AxiosProgressEvent = {
            total: this.file.size,
            loaded: this.uploadedChunks.length * FileUploadChunked.CHUNK_SIZE + progress.loaded,
            bytes: 0,
            lengthComputable: true,
          };
          fileUploadStore.handleUploadProgress(this.uid, fakeProgress);
        },
        signal: this.abortSignal,
      }
    )
      .then((uploadedChunk) => this.handleUploadedChunk(uploadedChunk))
      .catch((error) => {
        if (Axios.isCancel(error) || error === "ABORTED") {
          console.warn("request cancelled", error.message);
          this.handleAbort();
        }
      });
  }

  private handleAbort() {
    if (this.details.id > 0) {
      API.deleteData(API_URL.UPLOAD.abortChunkUpload(projectsDataStore.projectId, this.details.id))
        .then(() => this.reject(new Error("manually Aborted chunkupload " + this.details.id)))
        .catch((reason) => this.handleReject(reason, "could not abort chunkupload " + this.details.id));
    } else {
      this.reject(new Error("Aborted chunkupload"));
    }
  }

  private readNextChunk() {
    this.chunkNumber++;
    const blob = this.file.slice(this.offset, this.offset + FileUploadChunked.CHUNK_SIZE);
    const reader = new FileReader();
    this.log("read chunk " + this.chunkNumber + "");
    reader.onload = (event) => {
      if (event.target && event.target.result instanceof ArrayBuffer) {
        this.log("chunk " + this.chunkNumber + " loaded");
        const formData = new FormData();
        const chunkMd5 = new SparkMD5.ArrayBuffer();
        chunkMd5.append(event.target.result);
        const fileChunk = this.arrayBufferToFile(event.target.result, this.file.name, this.file.type);
        formData.append("file", fileChunk);
        formData.append("uploadId", this.details.id.toString());
        formData.append("size", fileChunk.size.toString());
        formData.append("md5", chunkMd5.end());
        formData.append("chunkNum", this.chunkNumber.toString());

        if (this.sendChunkPending) {
          // the previous read chunk is still uploading to BE, queue the next chunk
          this.log("chunk " + this.chunkNumber + " queued");
          this.queuedFormData = formData;
        } else {
          // otherwise start uploading the chunk and already start reading the pot. next chunk of the file
          this.sendChunk(formData);
          this.offset += fileChunk.size;
          if (this.offset < this.file.size) {
            this.readNextChunk();
          }
        }
      }
    };

    reader.onerror = (error) => this.handleReject(new Error("error reading a chunk "), error.toString());

    reader.readAsArrayBuffer(blob);
  }

  private arrayBufferToFile(arrayBuffer: ArrayBuffer, fileName: string, fileType: string): File {
    const blob = new Blob([arrayBuffer], { type: fileType });
    return new File([blob], fileName);
  }
}
